// src/native/ui/nativeChrome.js
//
// The handful of CSS behaviours that make a webview stop feeling like a
// webview. Guideline 4.2 is not cured by a checklist of native APIs, but the
// tells that make a reviewer say "this is a website in a wrapper" are cheap to
// remove and all of them live here:
//
//   - the grey tap flash Safari paints on every touched element,
//   - the long-press "Open Link / Copy / Share" callout on links and images,
//   - the rubber-band overscroll that exposes a bare white webview background
//     above the header and below the footer,
//   - iOS text inflation on rotation,
//   - a white page behind the app while a route chunk is still loading.
//
// Everything is injected at runtime, only when isNative() is true, into a
// single <style> element scoped under `html.ttt-native`. Nothing in the web
// stylesheet changes and nothing in this file executes in a browser.
//
// Deliberately no @capacitor/status-bar dependency: index.html already ships
// `apple-mobile-web-app-status-bar-style: black-translucent` and
// `viewport-fit=cover`, and capacitor.config.ts sets `contentInset: 'always'`,
// so the status bar is handled without another plugin to install and declare.

import { isNative, nativePlatform } from '@/lib/platform';

const STYLE_ID = 'ttt-native-chrome';
const ROOT_CLASS = 'ttt-native';

/** Matches capacitor.config.ts `ios.backgroundColor` and the index.html theme-color. */
export const NATIVE_SHELL_BACKGROUND = '#1a1d2e';

const CHROME_CSS = `
html.${ROOT_CLASS} {
  background-color: ${NATIVE_SHELL_BACKGROUND};
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
  -webkit-tap-highlight-color: transparent;
}

/* Stop the rubber band from revealing the bare webview behind the app. Vertical
   scrolling inside the page is untouched: this only kills the bounce at the
   document edges. */
html.${ROOT_CLASS},
html.${ROOT_CLASS} body {
  overscroll-behavior-y: none;
}

/* The long-press callout on links and images is the single loudest "this is a
   web page" tell. Text stays selectable and copyable on purpose: this is a
   communication app and people copy Timo's replies. */
html.${ROOT_CLASS} a,
html.${ROOT_CLASS} img,
html.${ROOT_CLASS} button,
html.${ROOT_CLASS} [role="button"] {
  -webkit-touch-callout: none;
}

/* Momentum scrolling inside every scroll container, not just the document. */
html.${ROOT_CLASS} body {
  -webkit-overflow-scrolling: touch;
}

/* Native apps do not show a text caret on things that are not text fields. */
html.${ROOT_CLASS} button,
html.${ROOT_CLASS} [role="button"] {
  cursor: default;
}

/* Safe area, exposed once so native-only components can use it without every
   file repeating the env() expression. */
html.${ROOT_CLASS} {
  --native-safe-top: env(safe-area-inset-top, 0px);
  --native-safe-bottom: env(safe-area-inset-bottom, 0px);
  --native-safe-left: env(safe-area-inset-left, 0px);
  --native-safe-right: env(safe-area-inset-right, 0px);
}
`;

/**
 * Apply the native chrome. Idempotent, and a no-op on the web.
 * @returns {boolean} true when the chrome was applied (native only).
 */
export function applyNativeChrome() {
  if (!isNative()) return false;
  if (typeof document === 'undefined') return false;

  const root = document.documentElement;
  root.classList.add(ROOT_CLASS);
  root.setAttribute('data-native-platform', nativePlatform());

  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CHROME_CSS;
    document.head.appendChild(style);
  }

  // Belt and braces for the moment between the splash going down and a lazy
  // route chunk arriving: paint the shell colour rather than white.
  root.style.backgroundColor = NATIVE_SHELL_BACKGROUND;

  return true;
}

/** Undo applyNativeChrome. Exists so the mounting component has a clean unmount. */
export function removeNativeChrome() {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.remove(ROOT_CLASS);
  root.removeAttribute('data-native-platform');
  root.style.removeProperty('background-color');
  document.getElementById(STYLE_ID)?.remove();
}
